import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Stack,
  Chip,
  Button,
  LinearProgress,
  IconButton,
  Dialog,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  PhotoCamera as PhotoIcon,
  TrendingUp as ProgressIcon,
  CompareArrows as CompareIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import usePhotoAnalysis from "../../../../../hooks/usePhotoAnalysis";
import ProgressPhotoCompareAnyTwoDialog from "../../../../../components/organisms/ProgressPhotoCompareAnyTwoDialog";
import ProgressPhotoGroupPanel from "../../../../../components/organisms/ProgressPhotoGroupPanel";
import PhotoAnalysisReport from "../../../../../components/organisms/PhotoAnalysisReport";
import { AIButton } from "../../../../../components";
import { downloadProgressPhotosZip } from "../../../../../utils/downloadProgressPhotosZip";
import { computeOrderedPhotoTypeEntries } from "../../../../../utils/progressPhotoTypeBreakdown";
import { getProgressPhotoTypeChipColor } from "../../../../../utils/progressPhotoTypeColor";
import useEntitlements from "../../../../../hooks/useEntitlements";
import UpgradePrompt from "../../../../../components/organisms/UpgradePrompt";
function formatDate(d) {
  if (!d) return "N/A";
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return "N/A";
  return x.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function chipColorForDisplayName(displayName, typeId) {
  const t = String(typeId || displayName || "").toLowerCase();
  return getProgressPhotoTypeChipColor(t);
}

const ActionButton = ({ children, ...props }) => (
  <Button
    variant={props.variant || "outlined"}
    sx={{
      borderRadius: 20,
      px: 2,
      py: 0.75,
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.8rem",
    }}
    {...props}
  >
    {children}
  </Button>
);

/**
 * Compressed Photo Progress + Progress Gallery for one client on the Media page.
 */
const MediaProgressPhotoClientSection = ({
  clientId,
  clientDisplayName,
  organizationId,
  flatPhotos,
  groupedPhotos,
  /** 'compact' | 'dense' — dense uses smaller tiles (Media client detail) */
  galleryVariant = "compact",
  referenceGroupId = null,
}) => {
  const [openCompare, setOpenCompare] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const { hasFeature, organizationData } = useEntitlements();
  const canAnalyzePhotos = hasFeature("ai_progress_photo_analysis");
  const [downloadProgress, setDownloadProgress] = React.useState(0);
  const [showAnalysisReport, setShowAnalysisReport] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "info",
  });

  const photoAnalysis = usePhotoAnalysis(clientId);

  const orderedPhotoTypes = React.useMemo(
    () => computeOrderedPhotoTypeEntries(flatPhotos),
    [flatPhotos],
  );

  const recentLatest = React.useMemo(() => {
    if (!flatPhotos?.length) return null;
    const sorted = [...flatPhotos].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
    return sorted[0]?.date;
  }, [flatPhotos]);

  const firstPhotoDate = React.useMemo(() => {
    if (!groupedPhotos?.length) return null;
    let minT = Infinity;
    let minDate = null;
    groupedPhotos.forEach((g) => {
      const t = new Date(g.date).getTime();
      if (!Number.isNaN(t) && t < minT) {
        minT = t;
        minDate = g.date;
      }
    });
    return minDate;
  }, [groupedPhotos]);

  const latestPhotoDate = React.useMemo(() => {
    if (!groupedPhotos?.length) return null;
    let maxT = -Infinity;
    let maxDate = null;
    groupedPhotos.forEach((g) => {
      const t = new Date(g.date).getTime();
      if (!Number.isNaN(t) && t > maxT) {
        maxT = t;
        maxDate = g.date;
      }
    });
    return maxDate;
  }, [groupedPhotos]);

  const handleDownload = React.useCallback(async () => {
    if (!flatPhotos?.length) {
      setSnackbar({
        open: true,
        message: "No photos to download",
        severity: "warning",
      });
      return;
    }
    try {
      setDownloading(true);
      setDownloadProgress(0);
      await downloadProgressPhotosZip({
        photos: flatPhotos,
        groupedPhotos,
        clientLabel: clientDisplayName || clientId,
        onProgress: setDownloadProgress,
      });
      setSnackbar({
        open: true,
        message: "Photos downloaded successfully",
        severity: "success",
      });
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        message: e?.message || "Download failed",
        severity: "error",
      });
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
    }
  }, [flatPhotos, groupedPhotos, clientDisplayName, clientId]);

  const handleAnalyze = React.useCallback(async () => {
    try {
      const result = await photoAnalysis.triggerAnalysis();
      if (result == null) return;
      setShowAnalysisReport(true);
      setSnackbar({
        open: true,
        message: "Photo analysis completed",
        severity: "success",
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e?.message || "Analysis failed",
        severity: "error",
      });
    }
  }, [photoAnalysis]);

  const summaryTo = `/home/clients/summary/${clientId}/${organizationId}`;

  return (
    <Stack spacing={2}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={{ xs: 1.25, sm: 1 }}
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1} flexShrink={0}>
              <PhotoIcon color="primary" sx={{ fontSize: { xs: 22, sm: 26 } }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Photo Progress
              </Typography>
            </Stack>
            <Button
              component={RouterLink}
              to={summaryTo}
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                alignSelf: { xs: "stretch", sm: "auto" },
                justifyContent: { xs: "center", sm: "center" },
                whiteSpace: "normal",
                textAlign: "center",
                py: 1,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                maxWidth: { sm: 360, md: 420 },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              {clientDisplayName}
            </Button>
          </Stack>
        </Box>
        <CardContent
          sx={{
            py: 2,
            px: { xs: 1.5, sm: 2 },
            "&:last-child": { pb: 2 },
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color="primary"
                  sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" } }}
                >
                  {flatPhotos.length}
                </Typography>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Total Photos
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Latest: {formatDate(recentLatest)}
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={5}>
              {flatPhotos.length > 0 ? (
                <>
                  <Typography
                    variant="caption"
                    fontWeight="medium"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75 }}
                  >
                    Photo Types
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {orderedPhotoTypes.map(([label, { count, type }]) => (
                      <Chip
                        key={label}
                        size="small"
                        label={`${label}: ${count}`}
                        color={chipColorForDisplayName(label, type)}
                      />
                    ))}
                  </Stack>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No photos in this snapshot
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={1} sx={{ width: "100%", minWidth: 0 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{
                    width: "100%",
                    "& .MuiButton-root": {
                      width: { xs: "100%", sm: "auto" },
                      minWidth: { sm: 0 },
                    },
                  }}
                >
                  <ActionButton
                    variant="contained"
                    color="primary"
                    startIcon={<CompareIcon />}
                    disabled={groupedPhotos.length < 2}
                    onClick={() => setOpenCompare(true)}
                  >
                    Compare groups
                  </ActionButton>
                  {downloading ? (
                    <Box
                      sx={{
                        width: { xs: "100%", sm: "100%" },
                        minWidth: { sm: 140 },
                        maxWidth: { sm: 280 },
                        position: "relative",
                      }}
                    >
                      <LinearProgress
                        variant="determinate"
                        value={downloadProgress}
                        sx={{ height: 32, borderRadius: 2 }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: "50%",
                          transform: "translateY(-50%)",
                          textAlign: "center",
                        }}
                      >
                        {downloadProgress}%
                      </Typography>
                    </Box>
                  ) : (
                    <ActionButton
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      disabled={!flatPhotos.length}
                      onClick={handleDownload}
                    >
                      Download All
                    </ActionButton>
                  )}
                  <Box sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 0 }}>
                    {canAnalyzePhotos ? (
                    <Stack spacing={0.75} sx={{ width: { xs: "100%", sm: "auto" } }}>
                      <AIButton
                        size="small"
                        loading={photoAnalysis.isAnalyzing}
                        loadingText="Generating…"
                        onClick={handleAnalyze}
                        disabled={!photoAnalysis.canAnalyze()}
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                      >
                        AI Analysis
                      </AIButton>
                      {photoAnalysis.isAnalyzing ? (
                        <>
                          <LinearProgress
                            color="primary"
                            sx={{ borderRadius: 1, height: 4 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Generating analysis — this can take a minute…
                          </Typography>
                        </>
                      ) : null}
                    </Stack>
                    ) : (
                      <UpgradePrompt
                        organizationData={organizationData}
                        minTier="pro"
                        variant="banner"
                        title="Upgrade for AI photo analysis"
                        description="Progress photo AI analysis is available on Pro."
                        featureName="ai_progress_photo_analysis"
                      />
                    )}
                  </Box>
                </Stack>
                {canAnalyzePhotos && !photoAnalysis.canAnalyze() && !photoAnalysis.isAnalyzing ? (
                  <Typography variant="caption" color="text.secondary">
                    Next analysis in {photoAnalysis.formatTimeRemaining()}
                  </Typography>
                ) : null}
              </Stack>
            </Grid>

            {flatPhotos.length > 0 ? (
              <Grid item xs={12}>
                <Box
                  sx={{
                    mt: 0.5,
                    pt: 1.5,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                  >
                    <Typography variant="caption" color="text.secondary">
                      First photo: {formatDate(firstPhotoDate)}
                    </Typography>
                    <Typography variant="caption" fontWeight="medium" color="primary">
                      {groupedPhotos.length} progress updates
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Latest: {formatDate(latestPhotoDate)}
                    </Typography>
                  </Stack>
                </Box>
              </Grid>
            ) : null}
          </Grid>
        </CardContent>
      </Card>

      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
          >
            <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
              <ProgressIcon
                color="primary"
                sx={{ fontSize: { xs: 22, sm: 26 }, flexShrink: 0 }}
              />
              <Typography variant="subtitle1" fontWeight="bold" noWrap={false}>
                Progress Gallery
              </Typography>
            </Stack>
            <IconButton
              component={RouterLink}
              to={summaryTo}
              size="small"
              aria-label="Open client in full profile"
              sx={{ flexShrink: 0 }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        <CardContent sx={{ py: 2, px: { xs: 1.5, sm: 2 } }}>
          {groupedPhotos.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No groups to display.
            </Typography>
          ) : (
            <Stack spacing={galleryVariant === "dense" ? 2 : 3}>
              {groupedPhotos.map((group) => (
                <Box key={group.groupId}>
                  <ProgressPhotoGroupPanel
                    group={group}
                    clientId={clientId}
                    isReferenceGroup={
                      !!referenceGroupId && group.groupId === referenceGroupId
                    }
                    variant={galleryVariant === "dense" ? "dense" : "compact"}
                    requireReveal={false}
                    showCoachNotes
                    showHeaderActions={false}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <ProgressPhotoCompareAnyTwoDialog
        open={openCompare}
        onClose={() => setOpenCompare(false)}
        groupedPhotos={groupedPhotos}
        referenceGroupId={referenceGroupId}
      />

      <Dialog
        open={showAnalysisReport}
        onClose={() => setShowAnalysisReport(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "95vw",
            height: "90vh",
            maxWidth: "1200px",
            maxHeight: "900px",
            borderRadius: 3,
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <PhotoAnalysisReport
            analysisData={photoAnalysis.data?.data}
            clientName={clientDisplayName || clientId}
            onClose={() => setShowAnalysisReport(false)}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default MediaProgressPhotoClientSection;
